import { useParams, Link } from 'react-router-dom';
import Helmet from '@/components/base/Helmet';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import CTASection from '@/pages/home/components/CTASection';
import { articleDetail } from '@/mocks/articles';
import { userComments } from '@/mocks/site';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = articleDetail;

  if (!article) {
    return (
      <>
        <Navbar />
        <div className="pt-20 pb-20 text-center">
          <p className="text-foreground-600">文章不存在</p>
          <Link to="/" className="text-primary-600 hover:underline mt-2 inline-block">返回首页</Link>
        </div>
        <Footer />
      </>
    );
  }

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: { '@type': 'Person', name: article.author },
    datePublished: article.date,
    articleSection: article.category,
    keywords: article.tags.join(','),
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'http://mengwenquda.cn/' },
      { '@type': 'ListItem', position: 2, name: article.category, item: `http://mengwenquda.cn/category/${article.categorySlug}` },
      { '@type': 'ListItem', position: 3, name: article.title, item: `http://mengwenquda.cn/article/${article.id}` },
    ],
  };

  return (
    <>
      <Helmet title={`${article.title} - 黑料爆料吃瓜网`} />
      <Helmet>
        {`<meta name="description" content="${article.excerpt}" />`}
        {`<meta name="keywords" content="${article.tags.join(',')},黑料爆料,吃瓜网" />`}
        {`<link rel="canonical" href="http://mengwenquda.cn/article/${article.id}" />`}
        {`<meta property="og:type" content="article" />`}
        {`<meta property="og:title" content="${article.title}" />`}
        {`<meta property="og:description" content="${article.excerpt}" />`}
        {`<meta property="article:published_time" content="${article.date}" />`}
        {`<meta property="article:author" content="${article.author}" />`}
        {`<script type="application/ld+json">${JSON.stringify(articleSchema)}</script>`}
        {`<script type="application/ld+json">${JSON.stringify(breadcrumbSchema)}</script>`}
      </Helmet>

      <Navbar />

      <main>
        <article className="w-full bg-background-50">
          <div className="relative w-full h-[360px] md:h-[480px] overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=Esports%20arena%20dramatic%20scene%20with%20empty%20gaming%20chairs%20under%20intense%20red%20spotlights%2C%20abandoned%20keyboards%20with%20investigation%20evidence%20markers%2C%20mysterious%20competitive%20gaming%20atmosphere%2C%20cinematic%20editorial%20photography%20with%20dramatic%20contrast%20and%20deep%20shadows%2C%20professional%20sports%20journalism%20style&width=1600&height=480&seq=article-hero-esports&orientation=landscape"
              alt={article.title}
              className="absolute inset-0 w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/30 to-black/80"></div>
            <div className="relative z-10 h-full flex flex-col justify-end px-4 md:px-6 pb-10 pt-16">
              <div className="max-w-4xl mx-auto w-full">
                <nav className="flex items-center gap-2 text-white/60 text-xs mb-4 flex-wrap">
                  <Link to="/" className="hover:text-white transition-colors whitespace-nowrap">首页</Link>
                  <i className="ri-arrow-right-s-line text-[10px]"></i>
                  <Link to={`/category/${article.categorySlug}`} className="hover:text-white transition-colors whitespace-nowrap">{article.category}</Link>
                  <i className="ri-arrow-right-s-line text-[10px]"></i>
                  <span className="text-white/80 whitespace-nowrap">正文</span>
                </nav>
                <h1 className="font-heading text-xl md:text-3xl font-black text-white leading-tight mb-3">
                  {article.title}
                </h1>
                <p className="text-white/70 text-sm max-w-2xl">{article.excerpt}</p>
                <div className="flex items-center gap-4 mt-4 text-white/50 text-xs flex-wrap">
                  <span className="flex items-center gap-1 whitespace-nowrap">
                    <i className="ri-user-line"></i> {article.author}
                  </span>
                  <span className="whitespace-nowrap">{article.date}</span>
                  <span className="flex items-center gap-1 whitespace-nowrap">
                    <i className="ri-eye-line"></i> {article.views.toLocaleString()} 阅读
                  </span>
                  <span className="flex items-center gap-1 whitespace-nowrap">
                    <i className="ri-chat-3-line"></i> {article.comments.toLocaleString()} 评论
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="max-w-4xl mx-auto px-4 md:px-6 py-10 md:py-14">
            <div className="flex flex-wrap gap-2 mb-8">
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium whitespace-nowrap"
                >
                  #{tag}
                </span>
              ))}
            </div>

            <div
              className="prose prose-sm md:prose-base max-w-none text-foreground-800 leading-relaxed
                [&_h2]:font-heading [&_h2]:text-xl [&_h2]:md:text-2xl [&_h2]:font-bold [&_h2]:text-foreground-950 [&_h2]:mt-8 [&_h2]:mb-4
                [&_p]:mb-4 [&_p]:leading-relaxed
                [&_strong]:text-foreground-900"
              dangerouslySetInnerHTML={{ __html: article.content }}
            ></div>
          </div>
        </article>

        <section className="w-full py-14 md:py-20 bg-background-100">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <h3 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-8">
              💬 网友评论
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {userComments.map((comment, idx) => (
                <div
                  key={comment.id}
                  className={`bg-background-50 rounded-lg p-5 border border-background-200/70 ${idx >= 4 ? 'opacity-50' : ''}`}
                >
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <i
                        key={i}
                        className={`text-xs ${i < comment.rating ? 'ri-star-fill text-accent-500' : 'ri-star-line text-foreground-300'}`}
                      ></i>
                    ))}
                  </div>
                  <p className="text-xs text-foreground-600 leading-relaxed mb-3">{comment.content}</p>
                  <div className="flex items-center gap-2.5">
                    <img src={comment.avatar} alt={comment.username} className="w-8 h-8 rounded-full object-cover" />
                    <div>
                      <p className="text-xs font-semibold text-foreground-900">{comment.username}</p>
                      <p className="text-[10px] text-foreground-500">{comment.date}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {article.relatedArticles && article.relatedArticles.length > 0 && (
          <section className="w-full py-14 md:py-20 bg-background-50">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <h3 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-8">
                📌 相关推荐
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {article.relatedArticles.map((related) => (
                  <Link
                    key={related.id}
                    to={`/article/${related.id}`}
                    className="group bg-background-100 rounded-lg overflow-hidden border border-background-200/70 hover:border-primary-200 transition-all"
                  >
                    <div className="relative h-36">
                      <div className="w-full h-full bg-gradient-to-br from-primary-100 to-accent-100 flex items-center justify-center">
                        <i className="ri-article-line text-3xl text-primary-400"></i>
                      </div>
                      <div className="absolute top-2 left-2">
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-primary-500 text-white whitespace-nowrap">
                          {related.category}
                        </span>
                      </div>
                    </div>
                    <div className="p-3">
                      <h4 className="font-semibold text-xs text-foreground-900 leading-snug line-clamp-2 group-hover:text-primary-600 transition-colors">
                        {related.title}
                      </h4>
                      <p className="text-[10px] text-foreground-500 mt-1.5 whitespace-nowrap">{related.date}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}

        <CTASection />
      </main>

      <Footer />
    </>
  );
}